import { useMutation } from '@tanstack/react-query';
import { Braces, Download, FileText } from 'lucide-react';
import { toast } from 'sonner';

import { ActionTooltip } from '@/_shared/components/ActionTooltip';
import { Button } from '@/_shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/_shared/components/ui/dropdown-menu';
import { useOrpc } from '@/_shared/hooks/useOrpc';

interface Props {
  chatId: string;
  /** Why there is nothing to download yet, or null when the chat can be downloaded. */
  disabledReason: string | null;
}

/** Hand the browser a file without a navigation: an object URL clicked through a detached anchor. */
function saveFile(content: string, filename: string, mimeType: string): void {
  const url = URL.createObjectURL(new Blob([content], { type: mimeType }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

/**
 * Download the chat as Markdown or JSON (ACT2-912). Shown to anyone who can read the chat — the
 * server admits exactly the readers `chat.get` admits — so a shared-chat viewer can download it too.
 */
export function ChatDownloadMenu({ chatId, disabledReason }: Props): React.ReactElement {
  const orpc = useOrpc();
  // A read, not a write: nothing to invalidate. A mutation because it runs on demand, never on mount,
  // and its result is a file handed to the browser, not state worth caching.
  const exportMutation = useMutation(
    orpc.chat.export.mutationOptions({
      onSuccess: (file) => saveFile(file.content, file.filename, file.mimeType),
      onError: (err) => toast.error(err.message || 'Could not download this chat'),
    }),
  );
  const disabled = disabledReason !== null;

  return (
    <DropdownMenu>
      <ActionTooltip show={disabled} message={disabledReason ?? ''}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="secondary"
            aria-label="Download chat"
            Icon={<Download />}
            disabled={disabled}
            loading={exportMutation.isPending}
          />
        </DropdownMenuTrigger>
      </ActionTooltip>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={() => exportMutation.mutate({ id: chatId, format: 'markdown' })}>
          <FileText className="mr-2 size-3.5" aria-hidden />
          Markdown (.md)
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => exportMutation.mutate({ id: chatId, format: 'json' })}>
          <Braces className="mr-2 size-3.5" aria-hidden />
          JSON (.json)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
